import { useCallback, useEffect } from "react";
import usePaginationStore from "../stores/pagination.store";

export interface UsePaginationOptions {
  pageType:
    | "warehouse"
    | "demands"
    | "entries"
    | "exits"
    | "codebook"
    | "employees"
    | "chooseCodebook";
  id?: string; // For pages that need an ID (demands, entries, exits)
  search?: string; // For pages that support search
  pageSize?: number;
}

export interface UsePaginationReturn {
  pageIndex: number;
  pageSize: number;
  setPage: (pageIndex: number) => void;
  nextPage: () => void;
  previousPage: () => void;
  resetPage: () => void;
  canNextPage: boolean;
  canPreviousPage: boolean;
  totalPages: number;
}

export const usePagination = ({
  pageType,
  id,
  search = "",
  pageSize = 10,
}: UsePaginationOptions): UsePaginationReturn => {
  const {
    warehouse,
    demands,
    entries,
    exits,
    codebook,
    employees,
    chooseCodebook,
    setWarehousePage,
    setDemandsPage,
    setEntriesPage,
    setExitsPage,
    setCodebookPage,
    setEmployeesPage,
    setChooseCodebookPage,
    resetWarehousePage,
    resetDemandsPage,
    resetEntriesPage,
    resetExitsPage,
    resetCodebookPage,
    resetEmployeesPage,
    resetChooseCodebookPage,
  } = usePaginationStore();

  // Get current page state based on page type
  const getCurrentState = () => {
    switch (pageType) {
      case "warehouse":
        return warehouse;
      case "demands":
        return demands;
      case "entries":
        return entries;
      case "exits":
        return exits;
      case "codebook":
        return codebook;
      case "employees":
        return employees;
      case "chooseCodebook":
        return chooseCodebook;
      default:
        return { pageIndex: 0, search: "" };
    }
  };

  // Get setter function based on page type
  const getSetter = useCallback(
    (pageIndex: number) => {
      switch (pageType) {
        case "warehouse":
          setWarehousePage(pageIndex, search);
          break;
        case "demands":
          if (id) setDemandsPage(pageIndex, id);
          break;
        case "entries":
          if (id) setEntriesPage(pageIndex, id);
          break;
        case "exits":
          if (id) setExitsPage(pageIndex, id);
          break;
        case "codebook":
          setCodebookPage(pageIndex, search);
          break;
        case "employees":
          setEmployeesPage(pageIndex, search);
          break;
        case "chooseCodebook":
          setChooseCodebookPage(pageIndex, search);
          break;
      }
    },
    [
      pageType,
      id,
      search,
      setWarehousePage,
      setDemandsPage,
      setEntriesPage,
      setExitsPage,
      setCodebookPage,
      setEmployeesPage,
      setChooseCodebookPage,
    ],
  );

  // Get reset function based on page type
  const getReset = useCallback(() => {
    switch (pageType) {
      case "warehouse":
        resetWarehousePage();
        break;
      case "demands":
        if (id) resetDemandsPage(id);
        break;
      case "entries":
        if (id) resetEntriesPage(id);
        break;
      case "exits":
        if (id) resetExitsPage(id);
        break;
      case "codebook":
        resetCodebookPage();
        break;
      case "employees":
        resetEmployeesPage();
        break;
      case "chooseCodebook":
        resetChooseCodebookPage();
        break;
    }
  }, [
    pageType,
    id,
    resetWarehousePage,
    resetDemandsPage,
    resetEntriesPage,
    resetExitsPage,
    resetCodebookPage,
    resetEmployeesPage,
    resetChooseCodebookPage,
  ]);

  const currentState = getCurrentState();
  const pageIndex = currentState.pageIndex;

  // Reset page when search changes (for searchable pages)
  useEffect(() => {
    if (
      ["warehouse", "codebook", "employees", "chooseCodebook"].includes(
        pageType,
      )
    ) {
      const currentSearch = (currentState as any).search || "";
      if (currentSearch !== search) {
        // Only reset if search actually changed
        if (currentSearch !== "" || search !== "") {
          getSetter(0);
        }
      }
    }
  }, [search, pageType, currentState, getSetter]);

  // Reset page when ID changes (for ID-based pages)
  useEffect(() => {
    if (["demands", "entries", "exits"].includes(pageType) && id) {
      const currentId = (currentState as any).id;
      if (currentId !== id) {
        getSetter(0);
      }
    }
  }, [id, pageType, currentState, getSetter]);

  const setPage = useCallback(
    (newPageIndex: number) => {
      getSetter(newPageIndex);
    },
    [getSetter],
  );

  const nextPage = useCallback(() => {
    getSetter(pageIndex + 1);
  }, [pageIndex, getSetter]);

  const previousPage = useCallback(() => {
    getSetter(pageIndex - 1);
  }, [pageIndex, getSetter]);

  const resetPage = useCallback(() => {
    getReset();
  }, [getReset]);

  return {
    pageIndex,
    pageSize,
    setPage,
    nextPage,
    previousPage,
    resetPage,
    canNextPage: false, // This will be set by the component using the hook
    canPreviousPage: pageIndex > 0,
    totalPages: 0, // This will be set by the component using the hook
  };
};
